import TranslationHistory from '../../components/apple-ui/TranslationHistory';
import { usePlan } from '../../contexts/PlanContext';
import ReferralToast from '../../components/apple-ui/ReferralToast';
import { shouldShowFirstFeatureReferral, markFirstFeatureUsed, FIRST_FEATURE_MSG } from '../../utils/referralTriggers';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import TranslationProgress from '../../components/TranslationProgress';
import { MainLayoutApple, CardApple, ButtonApple, BadgeApple, GlobalContextBarApple } from '@/components/apple-ui';
import CostEstimateApple from '@/components/apple-ui/CostEstimateApple';
import { 
  Languages, ArrowRightLeft, Copy, Loader2, Check, RefreshCw, Sparkles, Database,
  Search, BookOpen, Type, Hash, X, Clock, Trash2, RotateCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { useGlobalContext } from '@/contexts/GlobalContextProvider';
import ContextSelectorModal from '@/components/ContextSelectorModal';
import '@/styles/apple-design-system.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'Hindi' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'ta', name: 'Tamil' },
  { code: 'te', name: 'Telugu' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'mr', name: 'Marathi' },
  { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'bn', name: 'Bengali' },
  { code: 'or', name: 'Odia' },
  { code: 'as', name: 'Assamese' },
  { code: 'ur', name: 'Urdu' },
  { code: 'de', name: 'German' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
];

const getLanguageName = (code) => {
  const lang = LANGUAGES.find(l => l.code === code);
  return lang ? lang.name : code;
};

// Style options for translation
const STYLE_OPTIONS = [
  { value: 'general', label: 'General', desc: 'Balanced tone for everyday content' },
  { value: 'formal', label: 'Formal', desc: 'Professional business communication' },
  { value: 'casual', label: 'Casual', desc: 'Friendly conversational tone' },
  { value: 'technical', label: 'Technical', desc: 'Preserve technical terminology' },
  { value: 'creative', label: 'Creative', desc: 'More expressive language' },
  { value: 'legal', label: 'Legal', desc: 'Precise legal terminology' },
  { value: 'medical', label: 'Medical', desc: 'Medical/healthcare content' },
];

const getDictionaryUrl = (word, langCode) => {
  const query = encodeURIComponent(word);
  const shabdkoshLangs = { 'hi': 'hi', 'gu': 'gu', 'ta': 'ta', 'te': 'te', 'kn': 'kn', 'ml': 'ml', 'bn': 'bn', 'mr': 'mr', 'pa': 'pa' };
  if (shabdkoshLangs[langCode]) {
    return `https://www.shabdkosh.com/search-dictionary?lc=${shabdkoshLangs[langCode]}&sl=${shabdkoshLangs[langCode]}&tl=en&e=${query}`;
  }
  return `https://www.google.com/search?q=define+${query}`;
};

const BasicTranslationPageApple = ({ user, planInfo, onLogout, onPlanUpdate }) => {
  const { isDarkMode } = useTheme();
  const { refreshPlanInfo } = usePlan();
  const { showContextModal, setShowContextModal, getContextIds, selectedProject, selectedGlossary, selectedTM, selectedInstruction } = useGlobalContext();
  
  const [sourceText, setSourceText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [referralToast, setReferralToast] = useState(null);
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('hi');
  const [selectedStyle, setSelectedStyle] = useState('general');
  const [loading, setLoading] = useState(false);
  const [streamingPhase, setStreamingPhase] = useState(null); // null | 'draft' | 'refining' | 'done'
  const [translationStep, setTranslationStep] = useState(0); // 0=hidden, 1-4=active step
  const [wordCount, setWordCount] = useState(0);
  // Plan-aware word limit (free/starter: 1000, pro/business: 2000)
  const planWordLimit = ['professional', 'business', 'unlimited'].includes(user?.plan) ? 2000 : 1000;
  const wordLimitReached = wordCount > planWordLimit;
  const wordCountWarning = wordLimitReached
    ? { type: 'hard', msg: `${wordCount.toLocaleString()} words — Basic Translation supports up to ${planWordLimit.toLocaleString()} words. For longer documents, use the CAT Tool.` }
    : wordCount >= Math.floor(planWordLimit * 0.8)
    ? { type: 'soft', msg: `${wordCount.toLocaleString()} words — approaching the ${planWordLimit.toLocaleString()}-word limit. Use CAT Tool for large documents.` }
    : null;
  const [copied, setCopied] = useState(false);
  const [isTMMatch, setIsTMMatch] = useState(false);
  const [tmSuggestion, setTmSuggestion] = useState(null); // fuzzy TM match suggestion
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PER_PAGE = 10;
  const [saveToTM, setSaveToTM] = useState(false);
  const [tmMatchPending, setTmMatchPending] = useState(null); // {text, score}
  const [savingToTM, setSavingToTM] = useState(false);
  
  // Translation History state
  const [translationHistory, setTranslationHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Context menu states
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [showTargetMenu, setShowTargetMenu] = useState(false);
  const [selectedWord, setSelectedWord] = useState('');
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [menuAction, setMenuAction] = useState(null);
  const [processingWord, setProcessingWord] = useState(false);
  
  const sourceTextareaRef = useRef(null);
  const targetTextareaRef = useRef(null);
  const timerRefs = useRef([]);

  useEffect(() => {
    const words = sourceText.trim() ? sourceText.trim().split(/\s+/).length : 0;
    setWordCount(words);
  }, [sourceText]);

  useEffect(() => { loadTranslationHistory(); }, []);

  useEffect(() => {
    return () => timerRefs.current.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const handleClickOutside = () => { setShowSourceMenu(false); setShowTargetMenu(false); };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadTranslationHistory = async () => {
    setHistoryPage(1);
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/translation/history`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setTranslationHistory(data.history || []);
      }
    } catch (error) { console.error('Failed to load history:', error); }
    finally { setHistoryLoading(false); }
  };

  const getWordAtPosition = (textarea) => {
    const text = textarea.value;
    const cursorPos = textarea.selectionStart;
    let start = cursorPos, end = cursorPos;
    while (start > 0 && !/[\s,.!?;।\n]/.test(text[start - 1])) start--;
    while (end < text.length && !/[\s,.!?;।\n]/.test(text[end])) end++;
    return { word: text.substring(start, end).trim(), start, end };
  };

  const handleSourceDoubleClick = (e) => {
    e.stopPropagation();
    const textarea = sourceTextareaRef.current;
    if (!textarea) return;
    const { word } = getWordAtPosition(textarea);
    if (!word) return;
    setSelectedWord(word);
    setMenuPosition({ x: e.clientX, y: e.clientY });
    setShowSourceMenu(true);
    setShowTargetMenu(false);
  };

  const handleTargetDoubleClick = (e) => {
    e.stopPropagation();
    const textarea = targetTextareaRef.current;
    if (!textarea) return;
    const { word } = getWordAtPosition(textarea);
    if (!word) return;
    setSelectedWord(word);
    setMenuPosition({ x: e.clientX, y: e.clientY });
    if (e.altKey) { setMenuAction('romanize'); setShowTargetMenu(true); setShowSourceMenu(false); }
    else if (e.shiftKey) { setMenuAction('numeric'); setShowTargetMenu(true); setShowSourceMenu(false); }
    else { setMenuAction(null); setShowTargetMenu(true); setShowSourceMenu(false); }
  };

  const handleTransliterate = async () => {
    setProcessingWord(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/transliterate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: selectedWord, source_language: getLanguageName(sourceLang), target_language: getLanguageName(targetLang) })
      });
      const data = await response.json();
      if (response.ok && data.transliterated_text) {
        toast.success(<div><strong>Transliteration:</strong><div className="mt-1 text-lg">{selectedWord} → {data.transliterated_text}</div></div>, {
          duration: 5000,
          action: { label: 'Copy', onClick: () => { navigator.clipboard.writeText(data.transliterated_text); toast.success('Copied!'); } }
        });
      } else { toast.error(data.detail || 'Transliteration failed'); }
    } catch (error) { toast.error('Transliteration failed'); }
    finally { setProcessingWord(false); setShowSourceMenu(false); }
  };

  const handleRomanize = async () => {
    setProcessingWord(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/romanize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: selectedWord, source_language: getLanguageName(targetLang) })
      });
      const data = await response.json();
      if (response.ok && data.romanized_text) {
        const newText = translatedText.replace(selectedWord, data.romanized_text);
        setTranslatedText(newText);
        toast.success(`Romanized: ${selectedWord} → ${data.romanized_text}`);
      } else { toast.error(data.detail || 'Romanization failed'); }
    } catch (error) { toast.error('Romanization failed'); }
    finally { setProcessingWord(false); setShowTargetMenu(false); }
  };

  const handleTranslateFresh = async () => {
    setTmMatchPending(null);
    const token = localStorage.getItem('token');
    const contextIds = getContextIds();
    const langName = (code) => (LANGUAGES.find(l => l.code === code) || {}).name || code;
    setLoading(true);
    setTranslatedText('');
    setStreamingPhase(null);
    setTranslationStep(1);
    try {
      const response = await fetch(`${API_URL}/api/translate/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          source_language: langName(sourceLang),
          target_language: langName(targetLang),
          text: sourceText.trim(),
          style: selectedStyle,
          context_instruction_id: contextIds.instructionId || null,
          context_glossary_id: contextIds.glossaryId || contextIds.projectId || null,
          context_project_id: contextIds.projectId || null,
          save_to_tm: saveToTM,
          force_translate: true
        })
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const detail = errBody.detail;
        const errMsg = typeof detail === 'object' ? (detail?.message || 'Translation failed') : (detail || 'Translation failed');
        toast.error(errMsg);
        setTranslationStep(0);
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      setTranslationStep(2);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ') || line === 'data: [DONE]') continue;
          try {
            const data = JSON.parse(line.slice(6));
            console.log('[FRESH SSE]', data.type, Object.keys(data));
            if (data.type === 'draft_start') {
              setTranslationStep(2);
            } else if (data.type === 'draft_complete') {
              setTranslationStep(3);
            } else if (data.type === 'complete' && data.text) {
              setTranslatedText(data.text);
              setTranslationStep(4);
              timerRefs.current.push(setTimeout(() => setTranslationStep(0), 2000));
            } else if (data.type === 'complete' && data.translated_text) {
              setTranslatedText(data.translated_text);
              setTranslationStep(4);
              timerRefs.current.push(setTimeout(() => setTranslationStep(0), 2000));
            } else if (data.type === 'cache_hit' && data.text) {
              setTranslatedText(data.text);
              setTranslationStep(4);
              timerRefs.current.push(setTimeout(() => setTranslationStep(0), 2000));
            } else if (data.type === 'refine_token') {
              setTranslatedText(prev => prev + (data.token || data.text || ''));
            } else if (data.type === 'draft_token') {
              setTranslatedText(prev => prev + (data.token || data.text || ''));
            }
          } catch {}
        }
      }
    } catch (e) { console.error('Fresh translate error:', e); toast.error('Translation failed: ' + e.message); setTranslationStep(0); }
    finally { setLoading(false); setStreamingPhase(null); timerRefs.current.push(setTimeout(() => refreshPlanInfo(), 1500)); }
  };

    const handleTranslateNumeric = async () => {
    if (!/\d/.test(selectedWord)) { toast.error('Selected text does not contain numbers'); setShowTargetMenu(false); return; }
    setProcessingWord(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/translate-numerics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: selectedWord, target_language: getLanguageName(targetLang) })
      });
      const data = await response.json();
      if (response.ok && data.converted_text) {
        const newText = translatedText.replace(selectedWord, data.converted_text);
        setTranslatedText(newText);
        toast.success(`Numeric translated: ${selectedWord} → ${data.converted_text}`);
      } else { toast.error(data.detail || 'Numeric translation failed'); }
    } catch (error) { toast.error('Numeric translation failed'); }
    finally { setProcessingWord(false); setShowTargetMenu(false); }
  };

  const handleSearchGoogle = () => { window.open(`https://www.google.com/search?q=${encodeURIComponent(selectedWord)}`, '_blank'); setShowSourceMenu(false); };
  const handleSearchDictionary = () => { window.open(getDictionaryUrl(selectedWord, sourceLang), '_blank'); setShowSourceMenu(false); };

  const handleReuseTranslation = (historyItem) => {
    setSourceText(historyItem.source_text);
    setTranslatedText(historyItem.translated_text || historyItem.target_text);
    // Handle both API field names: source_language (API) and source_lang (legacy)
    const srcLang = historyItem.source_language || historyItem.source_lang;
    const tgtLang = historyItem.target_language || historyItem.target_lang;
    // Convert full language names to codes if needed
    const langCodeMap = { 'English': 'en', 'Hindi': 'hi', 'Gujarati': 'gu', 'Tamil': 'ta', 'Telugu': 'te', 'Punjabi': 'pa', 'Marathi': 'mr', 'Kannada': 'kn', 'Malayalam': 'ml', 'Bengali': 'bn', 'Odia': 'or', 'Assamese': 'as', 'Urdu': 'ur', 'German': 'de', 'French': 'fr', 'Spanish': 'es' };
    setSourceLang(langCodeMap[srcLang] || srcLang);
    setTargetLang(langCodeMap[tgtLang] || tgtLang);
    setIsTMMatch(historyItem.is_tm_match || false);
    toast.success('Translation loaded from history');
  };

  const handleDeleteHistory = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/translation/history/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setTranslationHistory(prev => prev.filter(item => (item._id || item.id) !== id));
      toast.success('Removed from history');
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleTranslate = async () => {
    if (!sourceText.trim()) { toast.error('Please enter text to translate'); return; }
    setLoading(true);
    setTranslatedText('');
    setIsTMMatch(false);
    setTmMatchPending(null);
    setStreamingPhase(null);
    setTranslationStep(1);
    
    try {
      const token = localStorage.getItem('token');
      const contextIds = getContextIds();
      const langName = (code) => (LANGUAGES.find(l => l.code === code) || {}).name || code;
      
      const response = await fetch(`${API_URL}/api/translate/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ 
          source_language: langName(sourceLang), 
          target_language: langName(targetLang), 
          text: sourceText.trim(),
          style: selectedStyle,
          context_instruction_id: contextIds.instructionId || null,
          context_glossary_id: contextIds.glossaryId || contextIds.projectId || null,
          context_project_id: contextIds.projectId || null,
          save_to_tm: saveToTM,
          force_translate: false
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        const detail = errData.detail;
        if (response.status === 400 && (typeof detail === 'object' ? detail?.error : null) === 'language_mismatch') {
          toast.error(`Language mismatch: ${typeof detail === 'object' ? detail.message : detail}`);
        } else {
          const errMsg = typeof detail === 'object' ? (detail?.message || 'Translation failed') : (detail || 'Translation failed');
          toast.error(errMsg);
        }
        setTranslationStep(0);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let draftText = '';
      let refinedText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ') || line === 'data: [DONE]') continue;
          
          try {
            const event = JSON.parse(line.slice(6));
            
            switch (event.type) {
              case 'cache_hit':
                setTranslatedText(event.text);
                setIsTMMatch(true);
                setStreamingPhase('done');
                setTranslationStep(4);
                timerRefs.current.push(setTimeout(() => setTranslationStep(0), 2000));
                toast.success('TM Match found - FREE translation!');
                break;
              case 'tm_hit':
                setStreamingPhase('done');
                setLoading(false);
                setTranslationStep(4);
                timerRefs.current.push(setTimeout(() => setTranslationStep(0), 2000));
                if ((event.similarity || 100) >= 100) {
                  // Exact match — auto-apply FREE
                  setTranslatedText(event.text);
                  setIsTMMatch(true);
                  toast.success('TM exact match - FREE translation!');
                } else {
                  // Fuzzy match — show suggestion modal
                  setTmSuggestion({ text: event.text, similarity: event.similarity, source: event.tm_source });
                }
                break;
              case 'draft_start':
                setStreamingPhase('draft');
                setTranslationStep(2);
                draftText = '';
                break;
              case 'draft_token':
                draftText += event.text;
                setTranslatedText(draftText);
                break;
              case 'draft_complete':
                setStreamingPhase('refining');
                setTranslationStep(3);
                refinedText = '';
                break;
              case 'refine_token':
                refinedText += event.text;
                setTranslatedText(refinedText);
                break;
              case 'complete':
                setTranslatedText(event.text);
                setStreamingPhase('done');
                setIsTMMatch(event.is_tm_match === true);
                setTranslationStep(4);
                timerRefs.current.push(setTimeout(() => setTranslationStep(0), 2000));
                if (!event.is_tm_match) {
                  toast.success(planInfo?.plan === 'payg' ? `Translated! ₹${((event.words_charged || wordCount) * 0.17).toFixed(2)} deducted` : `Translated ${event.words_charged || wordCount} words`);
                }
                if (event.updated_plan_info) {
                  if (onPlanUpdate) onPlanUpdate(event.updated_plan_info);
                  timerRefs.current.push(setTimeout(() => refreshPlanInfo(), 500));
                }
                loadTranslationHistory();
                if (shouldShowFirstFeatureReferral()) {
                  markFirstFeatureUsed();
                  setTimeout(() => setReferralToast(prev => prev || { message: FIRST_FEATURE_MSG, buttons: 'remind_later' }), 3000);
                }
                break;
              case 'error':
                toast.error(event.message || 'Translation failed');
                break;
            }
          } catch (parseErr) { /* skip non-JSON lines */ }
        }
      }
    } catch (error) {
      toast.error('Translation failed. Please try again.');
      setTranslationStep(0);
    } finally {
      setLoading(false);
      setStreamingPhase(null);
      timerRefs.current.push(setTimeout(() => refreshPlanInfo(), 1500));
    }
  };

  const handleSaveToTM = async () => {
    if (!sourceText.trim() || !translatedText.trim()) { toast.error('Both source and target text required'); return; }
    setSavingToTM(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/translation-memory/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ source_text: sourceText.trim(), target_text: translatedText.trim(), source_lang: sourceLang, target_lang: targetLang })
      });
      const data = await response.json();
      if (response.ok) { toast.success('Saved to Translation Memory!', { icon: '✓' }); }
      else { toast.error(data.detail || 'Failed to save to TM'); }
    } catch (error) { toast.error('Failed to save to TM'); }
    finally { setSavingToTM(false); }
  };

  const handleSwapLanguages = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
    setSourceText(translatedText);
    setTranslatedText('');
  };

  const handleCopy = async () => {
    if (!translatedText) return;
    try {
      await navigator.clipboard.writeText(translatedText);
      setCopied(true);
      toast.success('Copied to clipboard');
      timerRefs.current.push(setTimeout(() => setCopied(false), 2000));
    } catch (error) { toast.error('Failed to copy'); }
  };

  const handleClear = () => {
    setSourceText('');
    setTranslatedText('');
    setIsTMMatch(false);
    sourceTextareaRef.current?.focus();
  };

  return (
    <MainLayoutApple user={user} planInfo={planInfo} onLogout={onLogout}>
      <div className="apple-content" data-testid="apple-basic-translation-page">
        {/* Header */}
        <div className="apple-content-header">
          <h1 className="apple-content-title flex items-center gap-3">
            <Languages className="text-blue-600" size={28} />
            Basic Translation
          </h1>
          <p className="apple-content-subtitle">Quick text translation with formatting support</p>
        </div>

        {/* Global Context Bar */}
        <GlobalContextBarApple 
          onOpenModal={() => setShowContextModal(true)} 
          isDarkMode={isDarkMode}
        />

        {/* Context Selector Modal */}
        <ContextSelectorModal 
          isOpen={showContextModal} 
          onClose={() => setShowContextModal(false)} 
          variant="apple"
        />

        {/* Main Layout with History Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Translation Area */}
          <div className="lg:col-span-3">
            <CardApple>
              {/* Language Selector */}
              <CardApple.Header>
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
                    <select value={sourceLang} onChange={(e) => setSourceLang(e.target.value)} className="apple-input apple-select" data-testid="source-lang-select">
                      {LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
                    </select>
                  </div>
                  <button onClick={handleSwapLanguages} className="p-3 mt-5 hover:bg-gray-100 rounded-xl transition-colors" title="Swap languages" data-testid="swap-languages-btn">
                    <ArrowRightLeft size={20} className="text-gray-500" />
                  </button>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
                    <select value={targetLang} onChange={(e) => setTargetLang(e.target.value)} className="apple-input apple-select" data-testid="target-lang-select">
                      {LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Style</label>
                    <select value={selectedStyle} onChange={(e) => setSelectedStyle(e.target.value)} className="apple-input apple-select" data-testid="style-select">
                      {STYLE_OPTIONS.map(style => <option key={style.value} value={style.value}>{style.label}</option>)}
                    </select>
                  </div>
                </div>
              </CardApple.Header>

              <CardApple.Body className="p-0">
                {/* Translation progress timeline — above both columns, no layout shift */}
                {translationStep > 0 && (
                  <div className="border-b border-gray-100">
                    <TranslationProgress step={translationStep} />
                  </div>
                )}
                {/* Text Areas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                  {/* Source */}
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Source Text</span>
                      <span className="text-xs text-gray-400">{wordCount} words</span>
                    </div>
                    <textarea
                      ref={sourceTextareaRef}
                      value={sourceText}
                      onChange={(e) => setSourceText(e.target.value)}
                      onDoubleClick={handleSourceDoubleClick}
                      placeholder="Enter text to translate..."
                      className="w-full min-h-[250px] p-4 border border-gray-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                      data-testid="source-text-input"
                    />
                    {/* 3-zone toolbar: Left=Clear | Center=Info | Right=Actions */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 8 }}>
                      
                      {/* LEFT — destructive action */}
                      <button
                        onClick={handleClear}
                        disabled={!sourceText}
                        className="text-sm text-gray-400 hover:text-red-500 disabled:opacity-30 transition-colors"
                        data-testid="clear-btn"
                        title="Clear text"
                      >
                        Clear
                      </button>

                      {/* CENTER — word count + cost info */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'center' }}>
                        {wordCount > 0 && (
                          <span style={{ fontSize: 12, color: '#9ca3af' }}>
                            {wordCount} words
                            {planInfo?.plan === 'payg' && (
                              <span style={{ fontWeight: 700, color: '#2563eb', marginLeft: 6 }}>
                                · ≈ ₹{(wordCount * 0.17).toFixed(2)}
                              </span>
                            )}
                          </span>
                        )}
                        {!wordCount && (
                          <span style={{ fontSize: 11, color: '#d1d5db' }}>💡 Double-click a word for options</span>
                        )}
                      </div>

                      {/* RIGHT — save + primary action */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: '12px', color: 'var(--apple-text-secondary)', userSelect: 'none', whiteSpace: 'nowrap' }}>
                          <input
                            type="checkbox"
                            checked={saveToTM}
                            onChange={e => setSaveToTM(e.target.checked)}
                            style={{ width: '13px', height: '13px', accentColor: '#2563eb', cursor: 'pointer' }}
                          />
                          Save to TM
                        </label>
                        {wordCountWarning && (
                  <div style={{
                    padding: '8px 12px', borderRadius: '8px', marginBottom: '8px',
                    fontSize: '12px', fontWeight: 500,
                    display: 'flex', alignItems: 'flex-start', gap: '6px',
                    backgroundColor: wordCountWarning.type === 'hard' ? '#fef2f2' : '#fffbeb',
                    border: `1px solid ${wordCountWarning.type === 'hard' ? '#fca5a5' : '#fcd34d'}`,
                    color: wordCountWarning.type === 'hard' ? '#dc2626' : '#92400e',
                  }}>
                    <span>{wordCountWarning.type === 'hard' ? '🚫' : '⚠️'}</span>
                    <span>{wordCountWarning.msg}</span>
                  </div>
                )}
                <ButtonApple onClick={handleTranslate} disabled={loading || !sourceText.trim() || wordLimitReached} icon={loading ? Loader2 : Sparkles} data-testid="translate-btn">
                          {streamingPhase === 'draft' ? 'Drafting...' : streamingPhase === 'refining' ? 'Refining...' : loading ? 'Translating...' : 'Translate'}
                        </ButtonApple>
                      </div>

                    </div>
                  </div>

                  {/* Target - Editable */}
                  <div className="p-5 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Translation</span>
                        {streamingPhase === 'draft' && <BadgeApple variant="blue" size="sm"><Loader2 size={10} className="animate-spin mr-1" />Draft</BadgeApple>}
                        {streamingPhase === 'refining' && <BadgeApple variant="purple" size="sm"><Loader2 size={10} className="animate-spin mr-1" />Refining</BadgeApple>}
                        {isTMMatch && <BadgeApple variant="purple" size="sm">TM Match - FREE</BadgeApple>}
                        {isTMMatch && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                            background: 'rgba(124,58,237,0.08)', borderRadius: '10px',
                            border: '0.5px solid rgba(124,58,237,0.2)', marginTop: '6px'
                          }}>
                            <span style={{ fontSize: '12px', color: '#7c3aed', fontWeight: 500 }}>
                              Translation Memory match — served free
                            </span>
                            <button
                              onClick={handleTranslateFresh}
                              style={{
                                marginLeft: 'auto', fontSize: '11px', padding: '4px 10px',
                                borderRadius: '6px', background: 'transparent',
                                border: '0.5px solid #7c3aed', color: '#7c3aed',
                                cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap'
                              }}
                            >
                              Translate Fresh ↺
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {translatedText && (
                          <>

                            <button onClick={handleCopy} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700" data-testid="copy-btn">
                              {copied ? <Check size={14} /> : <Copy size={14} />}
                              {copied ? 'Copied' : 'Copy'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <textarea
                      ref={targetTextareaRef}
                      value={translatedText}
                      onChange={(e) => setTranslatedText(e.target.value)}
                      onDoubleClick={handleTargetDoubleClick}
                      placeholder={loading ? 'Translating...' : 'Translation will appear here... (editable)'}
                      disabled={loading}
                      className="w-full min-h-[250px] p-4 bg-white border border-gray-200 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base disabled:opacity-50"
                      data-testid="translated-text-output"
                    />
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-400">💡 Alt+Double-click: Romanize | Shift+Double-click: Numeric</span>
                      {translatedText && (
                        <button onClick={handleTranslate} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="retranslate-btn">
                          <RefreshCw size={14} />Re-translate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </CardApple.Body>
            </CardApple>

            {/* Tips Card */}
            <div className="mt-6 rounded-2xl p-5" style={{ background: isDarkMode ? '#2c2c2e' : '#f5f5f7' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(0,113,227,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>💡</div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: isDarkMode ? '#f5f5f7' : '#1d1d1f', margin: 0 }}>Pro Tips</h4>
              </div>
              <ul style={{ fontSize: '13px', lineHeight: 1.7, color: '#86868b', listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <li>• <strong style={{ color: isDarkMode ? '#f5f5f7' : '#1d1d1f' }}>Double-click</strong> any word in source for transliteration, Google search, or dictionary lookup</li>
                <li>• <strong style={{ color: isDarkMode ? '#f5f5f7' : '#1d1d1f' }}>Alt + Double-click</strong> word in target to romanize it</li>
                <li>• <strong style={{ color: isDarkMode ? '#f5f5f7' : '#1d1d1f' }}>Shift + Double-click</strong> numbers in target to translate numerics</li>
                <li>• Click <strong style={{ color: isDarkMode ? '#f5f5f7' : '#1d1d1f' }}>Save to TM</strong> to save translations for future reuse (FREE matches!)</li>
              </ul>
            </div>

            {/* Cost Estimate */}
            {wordCount > 0 && (
              <div className="mt-6">
                <CostEstimateApple planInfo={planInfo}
                  feature="translation"
                  sourceWords={wordCount}
                />
              </div>
            )}
          </div>

          {/* History Sidebar */}
          <div className="lg:col-span-1">
            <TranslationHistory
              history={translationHistory}
              loading={historyLoading}
              onReuse={handleReuseTranslation}
              onDelete={(id) => setTranslationHistory(prev => prev.filter(item => (item._id || item.id) !== id))}
              onRefresh={loadTranslationHistory}
            />
          </div>
        </div>
      </div>

      {/* Source Context Menu */}
      {showSourceMenu && (
        <div className="fixed bg-white rounded-2xl shadow-2xl border border-gray-200 py-2 z-50 min-w-[220px]" style={{ left: menuPosition.x, top: menuPosition.y }} onClick={(e) => e.stopPropagation()} data-testid="source-context-menu">
          <div className="px-4 py-2 border-b border-gray-100">
            <span className="text-xs text-gray-500">Selected:</span>
            <div className="font-semibold text-gray-900 truncate max-w-[200px]">{selectedWord}</div>
          </div>
          <button onClick={handleTransliterate} disabled={processingWord} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50" data-testid="source-menu-transliterate">
            {processingWord ? <Loader2 size={16} className="animate-spin" /> : <Type size={16} className="text-blue-500" />}
            Transliterate to {getLanguageName(targetLang)}
          </button>
          <button onClick={handleSearchGoogle} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50" data-testid="source-menu-google">
            <Search size={16} className="text-green-500" />Search in Google
          </button>
          <button onClick={handleSearchDictionary} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50" data-testid="source-menu-dictionary">
            <BookOpen size={16} className="text-purple-500" />Search in Dictionary
          </button>
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button onClick={() => setShowSourceMenu(false)} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-500 hover:bg-gray-50"><X size={16} />Cancel</button>
          </div>
        </div>
      )}

      {/* Target Context Menu */}
      {showTargetMenu && (
        <div className="fixed bg-white rounded-2xl shadow-2xl border border-gray-200 py-2 z-50 min-w-[220px]" style={{ left: menuPosition.x, top: menuPosition.y }} onClick={(e) => e.stopPropagation()} data-testid="target-context-menu">
          <div className="px-4 py-2 border-b border-gray-100">
            <span className="text-xs text-gray-500">Selected:</span>
            <div className="font-semibold text-gray-900 truncate max-w-[200px]">{selectedWord}</div>
          </div>
          {(menuAction === 'romanize' || menuAction === null) && (
            <button onClick={handleRomanize} disabled={processingWord} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50" data-testid="target-menu-romanize">
              {processingWord ? <Loader2 size={16} className="animate-spin" /> : <Type size={16} className="text-blue-500" />}
              Romanize to {getLanguageName(sourceLang)}
            </button>
          )}
          {(menuAction === 'numeric' || menuAction === null) && (
            <button onClick={handleTranslateNumeric} disabled={processingWord} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50" data-testid="target-menu-numeric">
              {processingWord ? <Loader2 size={16} className="animate-spin" /> : <Hash size={16} className="text-amber-500" />}
              Translate Numeric
            </button>
          )}
          <div className="border-t border-gray-100 mt-1 pt-1">
            <button onClick={() => setShowTargetMenu(false)} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-500 hover:bg-gray-50"><X size={16} />Cancel</button>
          </div>
        </div>
      )}

      {/* TM Suggestion Modal */}
      {tmSuggestion && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{backgroundColor:'white',borderRadius:'24px',boxShadow:'0 25px 50px rgba(0,0,0,0.25)',width:'100%',maxWidth:'480px',margin:'0 16px',padding:'32px'}}>
            <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'16px'}}>
              <span style={{fontSize:'24px'}}>🧠</span>
              <h3 style={{fontSize:'20px',fontWeight:600,margin:0}}>Found Similar Translation!</h3>
            </div>
            <p style={{fontSize:'14px',color:'#6b7280',marginBottom:'16px'}}>We found a {tmSuggestion.similarity}% match in your Translation Memory. Using it won't deduct any words.</p>
            <div style={{padding:'16px',backgroundColor:'#f5f3ff',borderRadius:'16px',marginBottom:'16px'}}>
              <div style={{fontSize:'11px',color:'#6b7280',marginBottom:'4px'}}>TM Match ({tmSuggestion.similarity}%):</div>
              <p style={{fontSize:'14px',color:'#1f2937',margin:0}}>{tmSuggestion.text?.substring(0, 300)}{tmSuggestion.text?.length > 300 ? '...' : ''}</p>
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:'12px'}}>
              <button onClick={() => { handleTranslateFresh(); setTmSuggestion(null); }} style={{padding:'10px 20px',borderRadius:'12px',border:'1px solid #e5e7eb',background:'white',cursor:'pointer',fontSize:'14px'}}>Translate Fresh</button>
              <button onClick={() => { setTranslatedText(tmSuggestion.text); setIsTMMatch(true); setTmSuggestion(null); toast.success(`TM match applied (${tmSuggestion.similarity}%) - FREE!`); }} style={{padding:'10px 20px',borderRadius:'12px',background:'#7c3aed',color:'white',border:'none',cursor:'pointer',fontSize:'14px',fontWeight:500}}>Use TM Match</button>
            </div>
          </div>
        </div>
      )}


      <ReferralToast
        toast={referralToast}
        onDismiss={() => setReferralToast(null)}
      />
    </MainLayoutApple>
  );
};

export default BasicTranslationPageApple;
